import {
   ChangeDetectionStrategy,
   Component,
   EventEmitter,
   Input,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { CallState } from '@lcs/shared/api';
import { ExtensionTypes, NullActionTypes } from 'generated/graphql-types';
import { ExtensionDetailView } from '../../extension-details/models/extension-detail-view.model';
import { AddTimeBasedRouteData } from '../../extension-time-based-routes/models/extension-time-based-routes.model';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-add-time-based-route-view',
    styleUrls: ['./add-time-based-route-view.component.scss'],
    templateUrl: './add-time-based-route-view.component.html',
    standalone: false
})
export class AddTimeBasedRouteViewComponent implements OnInit {
   @Input() callState: CallState;

   @Input() form: UntypedFormGroup;

   @Input() extension: ExtensionDetailView | null;

   @Input() title: string;

   @Output() formSubmitted = new EventEmitter<AddTimeBasedRouteData>();

   @Output() formCanceled = new EventEmitter<void>();

   @Output() dateToggleClicked = new EventEmitter<boolean>();

   @Output() timeToggleClicked = new EventEmitter<boolean>();

   public get availableNullActions(): NullActionTypes[] {
      if (this.extension?.type?.name === ExtensionTypes.Phone) {
         return [NullActionTypes.Voicemail, NullActionTypes.Unset];
      }
      return [NullActionTypes.Unset];
   }

   public get routeExtensionPlaceholder(): string {
      const canSelectVoicemail = this.availableNullActions.includes(NullActionTypes.Voicemail);
 
      if (canSelectVoicemail) {
         return this.hasExtensionOptions ? 'Select Extension or Voicemail' : 'Select Voicemail';
      }
 
      return 'Select Extension';
   }

   dateToggleChecked = false;

   timeToggleChecked = false;

   hasExtensionOptions = true;

   daysMap: Map<string, string>;

   ngOnInit() {
      this.form.get('startTime')?.value ? (this.timeToggleChecked = true) : this.form.get('startTime')?.disable();
      this.form.get('endTime')?.value ? (this.timeToggleChecked = true) : this.form.get('endTime')?.disable();
      this.form.get('startDate')?.value ? (this.dateToggleChecked = true) : (this.dateToggleChecked = false);
      this.form.get('endDate')?.value ? (this.dateToggleChecked = true) : (this.dateToggleChecked = false);

      this.daysMap = new Map<string, string>([
         ['isActiveMonday', 'Mo'],
         ['isActiveTuesday', 'Tu'],
         ['isActiveWednesday', 'We'],
         ['isActiveThursday', 'Th'],
         ['isActiveFriday', 'Fr'],
         ['isActiveSaturday', 'Sa'],
         ['isActiveSunday', 'Su'],
      ]);

      const days = Array.from(this.daysMap.keys());

      this.form.get('selectAll')?.valueChanges.subscribe((value: boolean) => {
         const daysOfWeekGroup = this.form.get('daysOfWeekGroup') as UntypedFormGroup;
         for (const key of Object.keys(daysOfWeekGroup.controls)) {
            daysOfWeekGroup.get(key)?.setValue(value, { emitEvent: false });
         }
      });
      this.form.get('daysOfWeekGroup')?.valueChanges.subscribe((values: { [key: string]: boolean }) => {
         for (const value of Object.values(values)) {
            if (!value) {
               this.form.get('selectAll')?.setValue(false, { emitEvent: false });
               return;
            }
         }
         this.form.get('selectAll')?.setValue(true, { emitEvent: false });
      });
   }

   onCloseClick(): void {
      this.formCanceled.emit();
   }

   onCreate(): void {
      if (this.form.valid) {
         this.formSubmitted.emit();
      } else {
         this.form.markAllAsTouched();
      }
   }

   onDateToggleClick(dateToggleChange: MatSlideToggleChange): void {
      this.form.patchValue({
         startDate: null,
         endDate: null,
      });
      this.dateToggleChecked = dateToggleChange.checked;
   }

   onTimeToggleClick(timeToggleChange: MatSlideToggleChange): void {
      this.form.patchValue({
         startTime: null,
         endTime: null,
      });
      timeToggleChange.checked ? this.form.get('startTime')?.enable() : this.form.get('startTime')?.disable();
      timeToggleChange.checked ? this.form.get('endTime')?.enable() : this.form.get('endTime')?.disable();
      this.timeToggleChecked = timeToggleChange.checked;
   }
   isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
   }

   onExtensionOptionsLoaded(options: ExtensionOption[]): void {
      this.hasExtensionOptions = options && options.length > 0;
   }
}
